import { Box, Group, UnstyledButton } from '@mantine/core';
import { useResizeObserver } from '@mantine/hooks';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { ReactNode, useId, useState } from 'react';

type Props = {
  children: ReactNode;
  maxHeight: number;
  fadeHeight?: number;
  showLabel?: ReactNode;
  hideLabel?: ReactNode;
};

const ExpandableContent = ({
  children,
  maxHeight,
  fadeHeight = 32,
  showLabel = 'Expand',
  hideLabel = 'Collapse',
}: Props) => {
  const [expanded, setExpanded] = useState(false);
  const contentId = useId();
  const [contentRef, contentRect] = useResizeObserver<HTMLDivElement>();
  const isOverflowing = contentRect.height > maxHeight;
  const shouldClip = !expanded && isOverflowing;
  const fadeStart = Math.max(0, maxHeight - Math.min(fadeHeight, maxHeight));

  return (
    <Box pos="relative" w="100%">
      <Box
        pos="relative"
        style={{
          maxHeight: shouldClip ? maxHeight : undefined,
          overflow: shouldClip ? 'hidden' : undefined,
          maskImage: shouldClip
            ? `linear-gradient(to bottom, #000 ${fadeStart}px, transparent)`
            : undefined,
          WebkitMaskImage: shouldClip
            ? `linear-gradient(to bottom, #000 ${fadeStart}px, transparent)`
            : undefined,
        }}
      >
        <Box ref={contentRef} id={contentId}>
          {children}
        </Box>
      </Box>
      {isOverflowing && (
        <Group justify="center" mt={6}>
          <UnstyledButton
            aria-controls={contentId}
            aria-expanded={expanded}
            onClick={() => setExpanded((value) => !value)}
            style={{
              alignItems: 'center',
              color: '#6B7280',
              display: 'inline-flex',
              fontSize: '12px',
              fontWeight: 500,
              gap: 2,
              lineHeight: 1.2,
            }}
          >
            {expanded ? hideLabel : showLabel}
            {expanded ? <IconChevronUp size={13} /> : <IconChevronDown size={13} />}
          </UnstyledButton>
        </Group>
      )}
    </Box>
  );
};

export default ExpandableContent;
